import { inject, Injectable } from '@angular/core';
import { AmlAdminDeskTranslationService } from '@core/services/aml-admin-translation.service';
import type { TrainingUserTableColumnsType } from '@features/academy/types/training-user-table-columns.type';
import type { ResponseTrainingUserDto } from '@kerberos-compliance/compliance-shared-ts/features/training/dtos/response.training-user.dto';
import { TrainingStatusMap } from '@kerberos-compliance/compliance-shared-ts/features/training/enums/training.enums';
import { TranslationsTrainingUserAccountStatus } from '@kerberos-compliance/compliance-shared-ts/features/training/i18n/training-user-account-status.translation';
import type { AmlAdminDeskFeatureTable } from '@kerberos-compliance/compliance-shared-ts/features/user/enums/feature-table.enum';
import { AmlAdminDeskFeatureTableMap } from '@kerberos-compliance/compliance-shared-ts/features/user/enums/feature-table.enum';
import type { TableColumn } from '@kerberos-compliance/compliance-shared-ui';
import { ColumnType, UserTablePreferencesService } from '@kerberos-compliance/compliance-shared-ui';

export const TrainingUserListColumnSortKeyMap = {
  LastName: 'individual.lastName',
  FirstName: 'individual.firstName',
  Email: 'email',
  ExpiresAt: 'expiresAt',
  AccountStatus: 'accountStatus',
} as const;

@Injectable()
export class TrainingUserListTableConfigService {
  private readonly translationService = inject(AmlAdminDeskTranslationService);

  private readonly tablePreferencesService =
    inject<UserTablePreferencesService<ResponseTrainingUserDto, AmlAdminDeskFeatureTable>>(UserTablePreferencesService);

  private readonly columnsTranslations = this.translationService.selectTranslateObject<TrainingUserTableColumnsType>(
    'academy.usersOverviewPage.columns',
  );

  private readonly activeLanguage = this.translationService.activeLanguage;

  /**
   * Returns the column configuration for the academy users table, applying the user's
   * saved preferences (order/visibility) when available.
   *
   * @returns {TableColumn<ResponseTrainingUserDto>[]} The column definitions with user preferences applied.
   */
  public getUserPreferredColumns(): TableColumn<ResponseTrainingUserDto>[] {
    const columns = this.getColumnsConfiguration();
    return this.tablePreferencesService.loadTablePreferences(columns, AmlAdminDeskFeatureTableMap.TrainingUser);
  }

  /**
   * Persists the current academy users table configuration (order, visibility).
   *
   * @param {TableColumn<ResponseTrainingUserDto>[]} columns - The current column configuration.
   * @returns {Promise<void>} Resolves once the configuration has been persisted.
   */
  public async saveTableConfiguration(columns: TableColumn<ResponseTrainingUserDto>[]): Promise<void> {
    await this.tablePreferencesService.saveTablePreferences(AmlAdminDeskFeatureTableMap.TrainingUser, columns);
  }

  /**
   * Builds the static column definitions for the academy users table.
   *
   * @returns {TableColumn<ResponseTrainingUserDto>[]} Column definitions.
   */
  private getColumnsConfiguration(): TableColumn<ResponseTrainingUserDto>[] {
    return [
      {
        title: () => this.columnsTranslations()?.lastName,
        key: 'lastName',
        data: (row: ResponseTrainingUserDto) => row.lastName ?? '',
        order: 0,
        visible: true,
        sort: {
          sortable: true,
          sortKey: TrainingUserListColumnSortKeyMap.LastName,
        },
        width: '9.375rem',
      },
      {
        title: () => this.columnsTranslations()?.firstName,
        key: 'firstName',
        data: (row: ResponseTrainingUserDto) => row.firstName ?? '',
        order: 1,
        visible: true,
        sort: {
          sortable: true,
          sortKey: TrainingUserListColumnSortKeyMap.FirstName,
        },
        width: '9.375rem',
      },
      {
        title: () => this.columnsTranslations()?.email,
        key: 'email',
        data: (row: ResponseTrainingUserDto) => row.email,
        order: 2,
        visible: true,
        sort: {
          sortable: true,
        },
        width: '12.5rem',
      },
      {
        title: () => this.columnsTranslations()?.accountStatus,
        key: 'accountStatus',
        data: (row: ResponseTrainingUserDto) =>
          TranslationsTrainingUserAccountStatus[this.activeLanguage()][row.accountStatus],
        order: 3,
        visible: true,
        sort: {
          sortable: true,
        },
      },
      {
        title: () => this.columnsTranslations()?.expiresAt,
        key: 'expiresAt',
        data: (row: ResponseTrainingUserDto) =>
          row.expiresAt ? '' : this.translationService.getTranslation('general.notSet'),
        type: ColumnType.Date,
        order: 4,
        visible: true,
        sort: {
          sortable: true,
        },
      },
      {
        title: () => this.columnsTranslations()?.trainingRecords,
        key: 'trainingRecords',
        type: ColumnType.Text,
        data: (row: ResponseTrainingUserDto) => {
          const finishedTrainings = row.trainingRecords.filter(
            (training) => training.status === TrainingStatusMap.Completed,
          ).length;
          const allTrainings = row.trainingRecords.length;
          return allTrainings > 0
            ? `${finishedTrainings} / ${allTrainings}`
            : this.translationService.getTranslation('general.none');
        },
        order: 5,
        visible: true,
      },
    ];
  }
}
